"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.currentReturnTo = exports.ssoCallbackParamOf = exports.SSO_CALLBACK_PARAMS = void 0;
const util_1 = require("@app/util");
/**
 * The five parameters the backend's OAuth callback can come back with. Since
 * `ssoBegin(returnTo)` landed they arrive on whichever page started the hop — any
 * allow-listed path, not a fixed `/auth/sso` — which is why they are read in one place
 * (`Routes.js`, above the `<Switch>`) instead of on each screen that offers an SSO
 * button. `/auth/sso` is still the *default* landing the backend falls back to when it
 * cannot read the state entry (the provider's own `?error=`, and `invalid_state`).
 */
exports.SSO_CALLBACK_PARAMS = [
    /** a session is waiting behind this code — single-use, 30s */
    'ssoExchange',
    /** identity verified, but the account also has MFA — 10 min */
    'ssoMfa',
    /** the provider or the callback refused; the value is a code, not a message */
    'ssoError',
    /** brand-new signup: nothing is provisioned yet, the company step is owed — 1 h */
    'ssoRegister',
    /** the invited address is not the one that signed in; nothing written yet — 10 min */
    'ssoInviteMismatch',
];
/**
 * The callback parameter this location carries, if any. First match wins — the backend
 * only ever sets one.
 *
 * `?ssoRegister=` is the exception to "handled centrally": it opens the registration
 * card's Společnost step, so the interceptor forwards it to `/registration` and then
 * stands aside, or it would forward the page to itself for ever.
 */
const ssoCallbackParamOf = (location) => {
    const params = new URLSearchParams(location.search ?? '');
    for (const name of exports.SSO_CALLBACK_PARAMS) {
        const value = params.get(name);
        if (!(0, util_1.isValue)(value) || value === '')
            continue;
        if (name === 'ssoRegister' && (location.pathname ?? '').startsWith('/registration')) {
            return undefined;
        }
        return { name, value };
    }
    return undefined;
};
exports.ssoCallbackParamOf = ssoCallbackParamOf;
/**
 * Where the provider's callback should put the browser back — this page, as it stands,
 * minus any callback parameter already on it (so a second attempt after `?ssoError=`
 * does not come back carrying the first attempt's failure).
 *
 * Absolute on purpose. The backend resolves a relative `returnTo` against
 * `core.frontend.url`, so `/login` from a dev server would send the person to the
 * *deployed* frontend instead. The origin has to be listed in
 * `core.sso.allowed_return_origins`, or `ssoBegin` refuses up front with
 * `sso.invalid_return_to`.
 */
const currentReturnTo = () => {
    const url = new URL(window.location.href);
    exports.SSO_CALLBACK_PARAMS.forEach((name) => url.searchParams.delete(name));
    url.hash = '';
    return url.href;
};
exports.currentReturnTo = currentReturnTo;
